import React, { useState, useEffect } from 'react';
import schedulingService from '../services/schedulingService';
import { userService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import useModal from '../hooks/useModal';

const CalendarPermissionsManager = () => {
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);

  const permissionLevels = [
    { value: 'full', label: 'Full Access', description: 'Can schedule anytime on calendar' },
    { value: 'business_hours', label: 'Business Hours', description: 'Can schedule during business hours only' },
    { value: 'emergency_only', label: 'Emergency Only', description: 'Can schedule only urgent/court deadline tasks' },
    { value: 'none', label: 'No Access', description: 'No scheduling permission' }
  ];

  useEffect(() => {
    loadPermissions();
  }, []);

  const loadPermissions = async () => {
    try {
      setLoading(true);
      const response = await schedulingService.getCalendarPermissions();
      setPermissions(response.data || []);
    } catch (err) {
      setError('Failed to load calendar permissions');
      console.error('Error loading permissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRevokePermission = async (permissionId) => {
    try {
      await schedulingService.revokeCalendarPermission(permissionId);
      await loadPermissions(); // Reload to show changes
    } catch (err) {
      setError('Failed to revoke permission');
      console.error('Error revoking permission:', err);
    }
  };

  const getPermissionLevelInfo = (level) => {
    return permissionLevels.find(p => p.value === level) || { label: level, description: '' };
  };

  const getPermissionIcon = (level) => {
    switch (level) {
      case 'full': return '🟢';
      case 'business_hours': return '🟡';
      case 'emergency_only': return '🟠';
      case 'none': return '🔴';
      default: return '❓';
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Calendar Permissions</h2>
          <p className="text-gray-600 mt-1">Manage who can schedule on your calendar</p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors"
        >
          Grant Permission
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {permissions.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <div className="text-4xl mb-4">📅</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Calendar Permissions</h3>
          <p className="text-gray-600 mb-4">You haven't granted anyone permission to schedule on your calendar yet.</p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="text-yellow-600 hover:text-yellow-700 font-medium"
          >
            Grant your first permission →
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {permissions.map((permission) => {
            const levelInfo = getPermissionLevelInfo(permission.permission_level);
            const isExpired = permission.expires_at && new Date(permission.expires_at) < new Date();
            
            return (
              <div key={permission.id} className="bg-white border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getPermissionIcon(permission.permission_level)}</span>
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {permission.grantee_name || `User ${permission.grantee_user_id}`}
                        </h4>
                        <p className="text-sm text-gray-500">{permission.grantee_email}</p>
                        <p className="text-sm text-gray-600">{levelInfo.label}</p>
                      </div>
                    </div>
                    
                    <div className="mt-2 text-sm text-gray-500">
                      <p>{levelInfo.description}</p>
                      {permission.expires_at && (
                        <p className={`${isExpired ? 'text-red-600' : 'text-gray-600'}`}>
                          {isExpired ? 'Expired' : 'Expires'}: {new Date(permission.expires_at).toLocaleDateString()}
                        </p>
                      )}
                      {permission.notes && (
                        <p className="italic">"{permission.notes}"</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      permission.is_valid && !isExpired
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {permission.is_valid && !isExpired ? 'Active' : 'Inactive'}
                    </span>
                    
                    <button
                      onClick={() => handleRevokePermission(permission.id)}
                      className="text-red-600 hover:text-red-800 text-sm font-medium"
                    >
                      Revoke
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreateForm && (
        <CreatePermissionModal
          onClose={() => setShowCreateForm(false)}
          onSuccess={loadPermissions}
          permissionLevels={permissionLevels}
        />
      )}
    </div>
  );
};

const CreatePermissionModal = ({ onClose, onSuccess, permissionLevels }) => {
  const { user: currentUser } = useAuth();
  const [formData, setFormData] = useState({
    grantee_user_id: '',
    permission_level: 'business_hours',
    expires_at: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [firmUsers, setFirmUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Modal behavior
  const { handleBackdropMouseDown, handleBackdropMouseUp } = useModal(true, onClose);

  // Load firm users when modal opens
  useEffect(() => {
    const loadFirmUsers = async () => {
      try {
        setLoadingUsers(true);
        const response = await userService.getUsers();
        const activeUsers = (response.data || []).filter(u => u.firm_active !== false && !u.has_pending_invitation);
        setFirmUsers(activeUsers);
      } catch (err) {
        console.error('Failed to load firm users:', err);
        setError('Failed to load firm users. Please try again.');
      } finally {
        setLoadingUsers(false);
      }
    };

    loadFirmUsers();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await schedulingService.createCalendarPermission({
        granter_user_id: currentUser.id, // Current user granting the permission
        grantee_user_id: formData.grantee_user_id,
        permission_level: formData.permission_level,
        expires_at: formData.expires_at || null,
        notes: formData.notes || null
      });
      
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create permission');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-[9999]"
      onMouseDown={handleBackdropMouseDown}
      onMouseUp={handleBackdropMouseUp}
    >
      <div className="fixed inset-x-0 top-16 bottom-0 bg-black bg-opacity-60 -z-10"></div>
      <div
        className="bg-white rounded-lg p-6 w-full max-w-md"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold text-gray-900 mb-4">Grant Calendar Permission</h3>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select User
            </label>
{loadingUsers ? (
              <div className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500">
                Loading users...
              </div>
            ) : (
              <SearchableUserDropdown
                users={firmUsers.filter(user => user.id !== currentUser?.id)}
                value={formData.grantee_user_id}
                onChange={(userId) => setFormData({ ...formData, grantee_user_id: userId })}
                placeholder="Search and select a user..."
              />
            )}
            <p className="text-xs text-gray-500 mt-1">
              Choose from users in your firm
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permission Level
            </label>
            <select
              value={formData.permission_level}
              onChange={(e) => setFormData({ ...formData, permission_level: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            >
              {permissionLevels.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              {permissionLevels.find(l => l.value === formData.permission_level)?.description}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expiration Date (Optional)
            </label>
            <input
              type="datetime-local"
              value={formData.expires_at}
              onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              rows="3"
              placeholder="Optional notes about this permission..."
            />
          </div>

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || loadingUsers || !formData.grantee_user_id}
              className="flex-1 bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors disabled:opacity-50"
            >
              {loading ? 'Creating...' : loadingUsers ? 'Loading...' : 'Grant Permission'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Searchable User Dropdown Component
const SearchableUserDropdown = ({ users, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = React.useRef(null);

  const selectedUser = users.find(user => user.id === value);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    return (
      user.first_name.toLowerCase().includes(searchLower) ||
      user.last_name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      (user.roles && user.roles.some(role => role.toLowerCase().includes(searchLower)))
    );
  });

  const handleSelect = (user) => {
    onChange(user.id);
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayText = selectedUser 
    ? `${selectedUser.first_name} ${selectedUser.last_name} (${selectedUser.email})`
    : placeholder;

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-left focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 bg-white flex items-center justify-between"
        required={!value}
      >
        <span className={selectedUser ? "text-gray-900" : "text-gray-500"}>
          {displayText}
        </span>
        <svg className={`w-5 h-5 text-gray-400 transform transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-hidden">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search users..."
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-yellow-500 focus:border-yellow-500"
              autoFocus
            />
          </div>
          
          <div className="max-h-48 overflow-y-auto">
            {filteredUsers.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500">
                No users found matching "{searchTerm}"
              </div>
            ) : (
              filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => handleSelect(user)}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100 focus:bg-gray-100 focus:outline-none text-sm"
                >
                  <div className="font-medium text-gray-900">
                    {user.first_name} {user.last_name}
                  </div>
                  <div className="text-gray-600">
                    {user.email}
                    {user.roles && user.roles.length > 0 && (
                      <span className="text-gray-500"> • {user.roles.join(', ')}</span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPermissionsManager;
